import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  operationsApi,
  type BomItemPayload,
  type ChantierListParams,
  type CreateChantierPayload,
  type CreateDNCPayload,
  type CreateLotPayload,
  type CreateMatierePayload,
  type CreateOrdrePayload,
  type CreateProduitPayload,
  type CreateTarifPayload,
  type CreateToupiePayload,
  type DNCListParams,
  type LotListParams,
  type MatiereListParams,
  type OrdreListParams,
  type OrdreTaskName,
  type OrdreTaskPayload,
  type PlanningParams,
  type ProduitListParams,
  type TarifListParams,
  type ToupieListParams,
  type UpdateChantierPayload,
  type UpdateDNCPayload,
  type UpdateLotPayload,
  type UpdateMatierePayload,
  type UpdateOrdrePayload,
  type UpdateProduitPayload,
  type UpdateTarifPayload,
  type UpdateToupiePayload,
  type VerifierNFPayload,
} from '@/api/operations';

// ── PRODUITS ────────────────────────────────────────────────────────────────

const PRODUITS_KEY = ['operations', 'beton', 'produits'] as const;
const GROUPES_KEY = ['operations', 'beton', 'catalogue-groupes'] as const;

/** Groupes de catalogue (24/10/2026) : liste ordonnée avec le nombre de produits. */
export const useCatalogueGroupes = () =>
  useQuery({ queryKey: GROUPES_KEY, queryFn: () => operationsApi.beton.listGroupes(), staleTime: 30_000 });

/** Créer, renommer, (dés)activer, ordonner : rafraîchit groupes et produits (la recherche porte sur le groupe). */
export const useGererGroupes = () => {
  const qc = useQueryClient();
  const rafraichir = () => { void qc.invalidateQueries({ queryKey: GROUPES_KEY }); void qc.invalidateQueries({ queryKey: PRODUITS_KEY }); };
  return {
    creer: useMutation({ mutationFn: (libelle: string) => operationsApi.beton.creerGroupe(libelle), onSuccess: rafraichir }),
    modifier: useMutation({
      mutationFn: ({ id, ...p }: { id: string; libelle?: string; actif?: boolean }) => operationsApi.beton.modifierGroupe(id, p),
      onSuccess: rafraichir,
    }),
    ordonner: useMutation({ mutationFn: (ids: string[]) => operationsApi.beton.ordonnerGroupes(ids), onSuccess: rafraichir }),
  };
};

export const useBetonProduits = (params: ProduitListParams = {}) =>
  useQuery({
    queryKey: [...PRODUITS_KEY, params],
    queryFn:  () => operationsApi.beton.listProduits(params),
    staleTime: 30_000,
  });

export const useBetonProduit = (id: string | undefined) =>
  useQuery({
    queryKey: [...PRODUITS_KEY, id],
    queryFn:  () => operationsApi.beton.getProduit(id!),
    enabled:  !!id,
    staleTime: 15_000,
  });

export const useCreateProduit = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateProduitPayload) => operationsApi.beton.createProduit(payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: PRODUITS_KEY }),
  });
};

export const useUpdateProduit = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateProduitPayload }) =>
      operationsApi.beton.updateProduit(id, payload),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: PRODUITS_KEY });
      qc.invalidateQueries({ queryKey: [...PRODUITS_KEY, id] });
    },
  });
};

export const useDeleteProduit = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => operationsApi.beton.deleteProduit(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: PRODUITS_KEY }),
  });
};

export const useReplaceBom = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, items }: { id: string; items: BomItemPayload[] }) =>
      operationsApi.beton.replaceBom(id, items),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: PRODUITS_KEY });
      qc.invalidateQueries({ queryKey: [...PRODUITS_KEY, id] });
    },
  });
};

// ── MATIÈRES PREMIÈRES ──────────────────────────────────────────────────────

const MATIERES_KEY = ['operations', 'beton', 'matieres'] as const;

export const useBetonMatieres = (params: MatiereListParams = {}) =>
  useQuery({
    queryKey: [...MATIERES_KEY, params],
    queryFn:  () => operationsApi.beton.listMatieres(params),
    staleTime: 60_000,
  });

export const useCreateMatiere = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateMatierePayload) => operationsApi.beton.createMatiere(payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: MATIERES_KEY }),
  });
};

export const useUpdateMatiere = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateMatierePayload }) =>
      operationsApi.beton.updateMatiere(id, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: MATIERES_KEY }),
  });
};

export const useDeleteMatiere = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => operationsApi.beton.deleteMatiere(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: MATIERES_KEY });
      qc.invalidateQueries({ queryKey: PRODUITS_KEY }); // re-fetch coût revient
    },
  });
};

// ── ORDRES DE FABRICATION (Sprint LB-B Production) ──────────────────────────

const ORDRES_KEY = ['operations', 'beton', 'ordres'] as const;

export const useOrdres = (params: OrdreListParams = {}) =>
  useQuery({
    queryKey: [...ORDRES_KEY, params],
    queryFn:  () => operationsApi.beton.listOrdres(params),
    staleTime: 15_000,
  });

export const useOrdre = (id: string | undefined) =>
  useQuery({
    queryKey: [...ORDRES_KEY, id],
    queryFn:  () => operationsApi.beton.getOrdre(id!),
    enabled:  !!id,
    staleTime: 10_000,
  });

export const useCreateOrdre = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateOrdrePayload) => operationsApi.beton.createOrdre(payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ORDRES_KEY }),
  });
};

export const useStartOrdre = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => operationsApi.beton.startOrdre(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ORDRES_KEY }),
  });
};

export const useCompleteOrdre = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, quantiteReelle }: { id: string; quantiteReelle?: number }) =>
      operationsApi.beton.completeOrdre(id, quantiteReelle),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ORDRES_KEY });
      qc.invalidateQueries({ queryKey: MATIERES_KEY }); // stocks MP modifiés
      qc.invalidateQueries({ queryKey: PRODUITS_KEY }); // stock produit modifié
    },
  });
};

export const useCancelOrdre = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => operationsApi.beton.cancelOrdre(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ORDRES_KEY }),
  });
};

export const useUpdateOrdre = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateOrdrePayload }) =>
      operationsApi.beton.updateOrdre(id, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ORDRES_KEY }),
  });
};

// ── Tâches préfa (Phase 1.3) ──────────────────────────────────────────
export const useMarkOrdreTask = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, task, payload = {} }: { id: string; task: OrdreTaskName; payload?: OrdreTaskPayload }) =>
      operationsApi.beton.markOrdreTask(id, task, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ORDRES_KEY });
      // décoffrage incrémente moule.nbCyclesEffectues
      qc.invalidateQueries({ queryKey: ['operations', 'beton', 'moules'] });
    },
  });
};

// ── CHANTIERS ──────────────────────────────────────────────────────────────

const CHANTIERS_KEY = ['operations', 'beton', 'chantiers'] as const;

export const useChantiers = (params: ChantierListParams = {}) =>
  useQuery({
    queryKey:  [...CHANTIERS_KEY, params],
    queryFn:   () => operationsApi.beton.listChantiers(params),
    staleTime: 30_000,
  });

export const useChantier = (id: string | undefined) =>
  useQuery({
    queryKey: [...CHANTIERS_KEY, id],
    queryFn:  () => operationsApi.beton.getChantier(id!),
    enabled:  !!id,
  });

export const useCreateChantier = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateChantierPayload) => operationsApi.beton.createChantier(payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: CHANTIERS_KEY }),
  });
};

export const useUpdateChantier = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateChantierPayload }) =>
      operationsApi.beton.updateChantier(id, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: CHANTIERS_KEY }),
  });
};

export const useDeleteChantier = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => operationsApi.beton.deleteChantier(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: CHANTIERS_KEY }),
  });
};

// ── TOUPIES (Flotte BPE) ────────────────────────────────────────────────────

const TOUPIES_KEY = ['operations', 'beton', 'toupies'] as const;

export const useToupies = (params: ToupieListParams = {}) =>
  useQuery({
    queryKey:  [...TOUPIES_KEY, params],
    queryFn:   () => operationsApi.beton.listToupies(params),
    staleTime: 30_000,
  });

export const useToupie = (id: string | undefined) =>
  useQuery({
    queryKey: [...TOUPIES_KEY, id],
    queryFn:  () => operationsApi.beton.getToupie(id!),
    enabled:  !!id,
    staleTime: 15_000,
  });

export const useCreateToupie = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateToupiePayload) => operationsApi.beton.createToupie(payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: TOUPIES_KEY }),
  });
};

export const useUpdateToupie = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateToupiePayload }) =>
      operationsApi.beton.updateToupie(id, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: TOUPIES_KEY }),
  });
};

export const useDeleteToupie = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => operationsApi.beton.deleteToupie(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: TOUPIES_KEY }),
  });
};

// ── PLANNING ────────────────────────────────────────────────────────────────

export const usePlanning = (params: PlanningParams = {}) =>
  useQuery({
    queryKey:  ['operations', 'beton', 'planning', params] as const,
    queryFn:   () => operationsApi.beton.getPlanning(params),
    staleTime: 60_000,
  });

// ── ESTIMATION pré-OF (LB-B v2.2) ───────────────────────────────────────────

export const useEstimation = (produitId: string | undefined, quantite: number) =>
  useQuery({
    queryKey:  ['operations', 'beton', 'estimation', produitId, quantite] as const,
    queryFn:   () => operationsApi.beton.getEstimation(produitId!, quantite),
    enabled:   !!produitId && quantite > 0,
    staleTime: 30_000,
  });

// ── LOTS DE PRODUCTION (LB-B v2) ────────────────────────────────────────────

const LOTS_KEY = ['operations', 'beton', 'lots'] as const;

export const useLotsByOrdre = (ordreId: string | undefined, params: LotListParams = {}) =>
  useQuery({
    queryKey:  [...LOTS_KEY, 'by-ordre', ordreId, params],
    queryFn:   () => operationsApi.beton.listLotsByOrdre(ordreId!, params),
    enabled:   !!ordreId,
    staleTime: 30_000,
  });

export const useAllLots = (params: LotListParams = {}) =>
  useQuery({
    queryKey:  [...LOTS_KEY, 'all', params],
    queryFn:   () => operationsApi.beton.listAllLots(params),
    staleTime: 30_000,
  });

export const useLot = (id: string | undefined) =>
  useQuery({
    queryKey:  [...LOTS_KEY, id],
    queryFn:   () => operationsApi.beton.getLot(id!),
    enabled:   !!id,
    staleTime: 30_000,
  });

export const useCreateLot = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ordreId, payload }: { ordreId: string; payload: CreateLotPayload }) =>
      operationsApi.beton.createLot(ordreId, payload),
    onSuccess: (_, { ordreId }) => {
      qc.invalidateQueries({ queryKey: [...LOTS_KEY, 'by-ordre', ordreId] });
    },
  });
};

export const useUpdateLot = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateLotPayload }) =>
      operationsApi.beton.updateLot(id, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: LOTS_KEY }),
  });
};

export const useDeleteLot = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => operationsApi.beton.deleteLot(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: LOTS_KEY }),
  });
};

export const useVerifierNF = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ lotId, payload }: { lotId: string; payload: VerifierNFPayload }) =>
      operationsApi.beton.verifierConformiteNF(lotId, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: LOTS_KEY }),
  });
};

// ── DNC (Déclarations Non-Conformité — LB-C) ───────────────────────────────

const DNC_KEY = ['operations', 'beton', 'dnc'] as const;

export const useDNCs = (params: DNCListParams = {}) =>
  useQuery({
    queryKey:  [...DNC_KEY, params],
    queryFn:   () => operationsApi.beton.listDNC(params),
    staleTime: 30_000,
  });

export const useCreateDNC = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateDNCPayload) => operationsApi.beton.createDNC(payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: DNC_KEY }),
  });
};

export const useUpdateDNC = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateDNCPayload }) =>
      operationsApi.beton.updateDNC(id, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: DNC_KEY }),
  });
};

export const useDeleteDNC = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => operationsApi.beton.deleteDNC(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: DNC_KEY }),
  });
};

// ── TARIFS CONTRATS CADRE (LB-D) ───────────────────────────────────────────

const TARIFS_KEY = ['operations', 'beton', 'tarifs-contrat-cadre'] as const;

export const useTarifs = (params: TarifListParams = {}) =>
  useQuery({
    queryKey:  [...TARIFS_KEY, params],
    queryFn:   () => operationsApi.beton.listTarifs(params),
    staleTime: 30_000,
  });

export const useCreateTarif = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateTarifPayload) => operationsApi.beton.createTarif(payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: TARIFS_KEY }),
  });
};

export const useUpdateTarif = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateTarifPayload }) =>
      operationsApi.beton.updateTarif(id, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: TARIFS_KEY }),
  });
};

export const useDeleteTarif = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => operationsApi.beton.deleteTarif(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: TARIFS_KEY }),
  });
};

// ── ALERTES BUSINESS (LB-E) — auto-refresh 60s ─────────────────────────────

export const useAlertes = () =>
  useQuery({
    queryKey:           ['operations', 'beton', 'alertes'] as const,
    queryFn:            () => operationsApi.beton.getAlertes(),
    staleTime:          30_000,
    refetchInterval:    60_000,           // refresh auto chaque 60s
    refetchIntervalInBackground: false,   // pause si onglet pas actif
  });
